import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";
import PageSEO from "../components/PageSEO";
import VimeoPlayer from "../components/VimeoPlayer";
import TypographyOverride from "../components/TypographyOverride";
import { useAudioMute } from "../lib/audioMute";

const isVimeoUrl = (u) => /(?:vimeo\.com|player\.vimeo\.com)/i.test(u || "");

const SITE_URL = "https://artistryperspectives.com";

export default function Home({ settings }) {
  const [works, setWorks] = useState([]);
  const [posts, setPosts] = useState([]);
  const heroRef = useRef(null);
  const videoRef = useRef(null);
  const vimeoPlayerRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  // Audio fades out a bit earlier than the visual so it feels natural — the
  // film "recedes" acoustically before it fully dissolves visually.
  const heroVolume = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    api.get("/works", { params: { featured: true } }).then(({ data }) => setWorks(data));
    api.get("/blogs?status=published")
      .then(({ data }) => setPosts(data || []))
      .catch(() => {});
  }, []);

  const headline = settings?.hero_headline || "We make\nwork worth";
  const subtext = settings?.hero_subtext || "";
  const heroVideoDesktop = settings?.hero_mp4_fallback_url || "";
  const heroVideoMobile = settings?.hero_mp4_fallback_url_mobile || "";
  // Track viewport <768px so we can serve the dedicated portrait mobile
  // hero video (Item 4). Falls back to the desktop video if no mobile
  // upload exists, so existing installs keep working unchanged.
  const [isMobileVp, setIsMobileVp] = useState(
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(max-width: 767px)").matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobileVp(e.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange);
    };
  }, []);
  const heroVideo = (isMobileVp && heroVideoMobile) ? heroVideoMobile : heroVideoDesktop;
  const heroIsVimeo = useMemo(() => isVimeoUrl(heroVideo), [heroVideo]);
  const { muted: audioMuted, toggle: toggleMute } = useAudioMute();

  // Hero click destination — computed from admin settings.
  // Type: "none" | "blog" | "case-study" | "work" | "external"
  const heroClickType = settings?.hero_click_type || "none";
  const heroClickTarget = (settings?.hero_click_target || "").trim();
  const heroLink = (() => {
    if (!heroClickTarget || heroClickType === "none") return null;
    if (heroClickType === "external") return { href: heroClickTarget, external: true };
    if (heroClickType === "blog") return { to: `/blog/${heroClickTarget}` };
    if (heroClickType === "case-study") return { to: `/work/${heroClickTarget}/case-study` };
    if (heroClickType === "work") return { to: `/work#${heroClickTarget}` };
    return null;
  })();
  const fallbackImg = settings?.hero_fallback_image ||
    "https://images.unsplash.com/photo-1770745560263-a8fc696de90b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjB0ZXh0dXJlJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3NzcwMjc2MDR8MA&ixlib=rb-4.1.0&q=85";
  const cycleWords = (settings?.hero_cycle_words || "keeping,watching,lasting")
    .split(",")
    .map((w) => w.trim())
    .filter(Boolean);

  // reset readiness if the video URL changes & nudge play() for iOS/Android safety
  useEffect(() => {
    setVideoReady(false);
    if (!heroVideo) return;
    const v = videoRef.current;
    if (!v) return;
    // Some mobile browsers need an explicit play() after src changes
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    tryPlay();
    // Retry once on visibility change (iOS pauses on tab switch)
    const onVis = () => { if (!document.hidden) tryPlay(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [heroVideo]);

  const markVideoReady = () => setVideoReady(true);

  // Fade audio volume in sync with the visual scroll-fade. Uses imperative
  // SDK / video-element calls so we don't re-render on every scroll pixel.
  useMotionValueEvent(heroVolume, "change", (v) => {
    if (audioMuted) return; // nothing to fade if the user hasn't unmuted
    const clamped = Math.max(0, Math.min(1, v));
    const p = vimeoPlayerRef.current;
    if (p) {
      try { p.setVolume(clamped); } catch {}
    }
    if (videoRef.current) {
      try { videoRef.current.volume = clamped; } catch {}
    }
  });

  return (
    <main className="home-page">
      {/* Per-zone typography overrides for the home page — editable in
         Typography → Home admin. Independent mobile / desktop sliders per
         zone let you tune Android sizing separately. */}
      <TypographyOverride pageId="home" scopeSelector=".home-page" prefix="hm" />
      <PageSEO
        title="Artistry Perspectives — Cinematic Brand Films, Strategy & Digital Experiences"
        description="Artistry Perspectives crafts cinematic brand films, strategic campaigns, and digital experiences for modern companies."
        image={`${SITE_URL}/og-image.jpg`}
        url={SITE_URL + "/"}
      />
      {/* ---------- HERO ---------- */}
      <section
        ref={heroRef}
        data-testid="home-hero"
        className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-[#0A0A0C]"
      >
        {/* Layer 1 — fallback image. ALWAYS rendered, ALWAYS visible.
           `fetchpriority="high"` promotes this image ahead of every other
           network request so it's on screen before the Vimeo frame catches
           up. Width/height are representative intrinsic dimensions used by
           the browser to compute aspect ratio while decoding — the CSS
           `object-cover` still controls final layout. */}
        <img
          src={fallbackImg}
          alt=""
          aria-hidden="true"
          data-testid="hero-fallback-image"
          fetchpriority="high"
          width={1920}
          height={1080}
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ zIndex: 0 }}
        />

        {/* Layer 2 — optional video enhancement. Accepts either an MP4
           (raw <video>) or a Vimeo URL (VimeoPlayer via SDK). In both
           cases the mute state is controlled by the global AudioMute
           toggle below. Fades in when ready, never replaces fallback. */}
        {heroVideo && heroIsVimeo && (
          <motion.div
            aria-hidden="true"
            data-testid="hero-vimeo"
            className="absolute inset-0 w-full h-full"
            style={{ zIndex: 1, opacity: heroOpacity }}
          >
            <VimeoPlayer
              url={heroVideo}
              fallback="-"
              orientation="horizontal"
              respectMute
              inlineToggle={false}
              visibilityFade={false}
              onPlayerReady={(p) => { vimeoPlayerRef.current = p; }}
              objectFit="cover"
              premium={false}
              testid="hero"
            />
          </motion.div>
        )}
        {heroVideo && !heroIsVimeo && (
          <motion.div
            aria-hidden="true"
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ zIndex: 1, opacity: heroOpacity }}
          >
            <video
              ref={videoRef}
              data-testid="hero-mp4"
              src={heroVideo}
              autoPlay
              muted={audioMuted}
              loop
              playsInline
              // iOS Safari requires both attributes for inline autoplay
              // eslint-disable-next-line react/no-unknown-property
              webkit-playsinline="true"
              preload="auto"
              disablePictureInPicture
              disableRemotePlayback
              controls={false}
              tabIndex={-1}
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                opacity: videoReady ? 1 : 0,
                transition: "opacity 600ms ease",
              }}
              onLoadedData={markVideoReady}
              onCanPlay={markVideoReady}
              onPlaying={markVideoReady}
              onError={() => setVideoReady(false)}
            />
          </motion.div>
        )}

        {/* Layer 3 — readability gradient overlay (sits between video and text) */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            zIndex: 2,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.75) 100%)",
          }}
        />
        {/* Subtle film grain — decorative, kept below text */}
        <div
          aria-hidden="true"
          className="absolute inset-0 hero-grain-layer pointer-events-none opacity-[0.07]"
          style={{ zIndex: 2 }}
        />

        {/* Click destination overlay — only present when admin has configured
            a target. Covers the whole hero (except the content column) so
            users can tap anywhere on the video to navigate. */}
        {heroLink && (
          heroLink.external ? (
            <a
              href={heroLink.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-click-external"
              aria-label="Featured piece"
              className="absolute inset-0 z-[2] cursor-pointer"
            />
          ) : (
            <Link
              to={heroLink.to}
              data-testid="hero-click-internal"
              aria-label="Featured piece"
              className="absolute inset-0 z-[2] cursor-pointer"
            />
          )
        )}

        {/* Audio mute/unmute toggle — INSIDE the hero, sticks with it
           during scroll. Bottom-LEFT so it never collides with the site-wide
           WhatsApp button (bottom-right). Rendered for both MP4 heroes and
           Vimeo heroes (Vimeo's inlineToggle is disabled so this single
           button controls the shared global mute state either way).
           Sits at z-[5], safely above the z-[2] click overlay so it's
           always clickable. */}
        {heroVideo && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleMute(); }}
            aria-label={audioMuted ? "Turn hero sound on" : "Turn hero sound off"}
            aria-pressed={!audioMuted}
            data-testid="hero-audio-mute-toggle"
            className={`absolute bottom-6 left-6 md:bottom-8 md:left-8 z-[5] w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-md border ${
              audioMuted
                ? "bg-black/50 border-white/25 text-white/80 hover:text-white hover:border-white/60"
                : "bg-[#C9A050] border-[#C9A050] text-black hover:brightness-110"
            }`}
          >
            {audioMuted ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M11 5 6 9H2v6h4l5 4V5Z" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M11 5 6 9H2v6h4l5 4V5Z" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
          </button>
        )}

        <motion.div
          style={{ opacity: heroOpacity, zIndex: 3 }}
          className="relative h-full flex flex-col justify-end px-6 md:px-14 lg:px-24 pb-20 md:pb-32"
        >
          <h1
            data-testid="hero-headline"
            className="font-display text-[14vw] sm:text-[12vw] md:text-[10vw] lg:text-[8.5vw] leading-[0.84] tracking-[-0.04em]"
          >
            {headline.split("\n").map((line, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 80 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                className="block"
              >
                {line}
              </motion.span>
            ))}
            {cycleWords.length > 0 && (
              <motion.span
                initial={{ opacity: 0, y: 80 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 0.25 + (headline.split("\n").length) * 0.14, ease: [0.16, 1, 0.3, 1] }}
                className="block leading-[0.92] pb-0 mt-4 md:mt-0"
                style={{ overflow: "visible" }}
              >
                <CyclingItalicWord words={cycleWords} />
              </motion.span>
            )}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-4 md:mt-6 flex items-end justify-between gap-8"
          >
            <div className="max-w-[420px] md:max-w-lg">
              <p data-testid="hero-subtext" className="text-[#C5C5CC] leading-[1.65] text-[15px] md:text-base font-normal whitespace-pre-line">
                {subtext}
              </p>
              {settings?.hero_cta_text && (
                <Link
                  to={settings?.hero_cta_href || "/work"}
                  data-testid="hero-cta"
                  data-cursor="View"
                  className="mt-8 md:mt-10 inline-flex items-center gap-4 group"
                >
                  <span className="overline text-white group-hover:text-[#C9A050] transition-colors">
                    {settings.hero_cta_text}
                  </span>
                  <span className="h-px w-12 bg-white/30 group-hover:bg-[#C9A050] group-hover:w-20 transition-all duration-500" />
                </Link>
              )}
            </div>
            <div className="hidden md:flex items-center gap-4 shrink-0">
              <span className="h-px w-10 bg-white/25" />
              <span className="overline text-[10px]">Scroll</span>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ---------- MARQUEE STRIP ---------- */}
      {(() => {
        const marqueeWords = (settings?.marquee_words || "Identity,Film,Editorial,Digital,Art Direction,Campaigns,Strategy")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        return (
          <section className="border-y border-white/5 overflow-hidden py-7 md:py-8" data-testid="marquee">
            <div className="flex marquee-track whitespace-nowrap">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="flex items-center shrink-0" aria-hidden={i === 1}>
                  {marqueeWords.map((t) => (
                    <span key={t} className="font-display text-4xl md:text-6xl text-[#6A6A73] tracking-[-0.02em] flex items-center">
                      <span>{t}</span>
                      <span className="text-white/20 mx-10 md:mx-14 font-serif-accent">·</span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      {/* ---------- CLIENT LOGO STRIP — horizontal scrollable, snap-aligned ---------- */}
      {settings?.clients_visible && settings?.clients?.length > 0 && (
        <section data-testid="clients-strip" className="py-20 md:py-28 border-b border-white/5">
          <p className="overline text-center mb-12">{settings.clients_heading || "Trusted by"}</p>
          <div className="relative">
            {/* soft right-edge fade hint that there's more to scroll */}
            <div
              aria-hidden="true"
              className="absolute right-0 top-0 bottom-0 w-12 md:w-20 pointer-events-none z-10"
              style={{ background: "linear-gradient(to left, #0A0A0C, transparent)" }}
            />
            <div
              className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-none px-6 md:px-14 lg:px-24 pb-2"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {settings.clients.map((c, i) => (
                <div
                  key={i}
                  data-testid={`client-logo-${i}`}
                  className="snap-start shrink-0 bg-white rounded-xl flex items-center justify-center w-[78px] h-[78px] md:w-[140px] md:h-[140px] p-2 md:p-3 shadow-[0_4px_20px_rgba(0,0,0,0.18)] hover:shadow-[0_8px_32px_rgba(201,160,80,0.18)] hover:-translate-y-0.5 transition-all duration-500"
                >
                  <img
                    src={c.logo_url}
                    alt={c.name}
                    loading="lazy"
                    className="max-h-full max-w-full w-auto h-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- FEATURED WORK — asymmetric, 1 dominant + 2 quieter ---------- */}
      <section data-testid="featured-work" className="px-6 md:px-14 lg:px-24 py-36 md:py-64">
        <div className="grid grid-cols-12 mb-28 md:mb-44 gap-6">
          <Reveal className="col-span-12 md:col-span-7 md:col-start-2">
            <p className="overline mb-8">Selected work — {works.length || 0}</p>
            <h2 data-testid="featured-heading" className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-[-0.035em]">
              {settings?.featured_heading_top || "A small body"}<br />
              <span className="font-serif-accent text-[#C9A050]">{settings?.featured_heading_italic || "of work,"}</span><br />
              {settings?.featured_heading_bottom || "carefully chosen."}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 md:col-span-3 md:col-start-10 md:self-end">
            <p data-testid="featured-paragraph" className="text-[#C5C5CC] text-base md:text-[17px] leading-[1.7] font-normal whitespace-pre-line">
              {settings?.featured_paragraph || "Every project here was partnered with a founder or creative director who wanted something that would last a decade — not a season."}
            </p>
            <Link
              to="/work"
              data-testid="all-work-link"
              data-cursor="View"
              className="mt-10 inline-flex items-center gap-4 group"
            >
              <span className="overline text-white group-hover:text-[#C9A050] transition-colors">{settings?.featured_cta_label || "All projects"}</span>
              <span className="h-px w-12 bg-white/25 group-hover:bg-[#C9A050] group-hover:w-24 transition-all duration-500" />
            </Link>
          </Reveal>
        </div>

        {/* WORK GRID — same staggered editorial pattern as /work, 4 featured cards */}
        <div data-testid="home-work-grid" className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-16 md:gap-y-24">
          {works.slice(0, 4).map((w, idx) => {
            const vertical = w.orientation === "vertical";
            const pattern = vertical
              ? ["md:col-span-4", "md:col-span-4 md:mt-20", "md:col-span-4", "md:col-span-4 md:mt-12"]
              : ["md:col-span-7", "md:col-span-5 md:mt-24", "md:col-span-5", "md:col-span-7 md:mt-16"];
            const span = pattern[idx % pattern.length];
            const frame = vertical ? "aspect-[9/16]" : "aspect-[4/5]";
            const href = w.slug ? `/work/${w.slug}` : "/work";
            return (
              <Reveal key={w.id} delay={(idx % 3) * 0.08} className={span}>
                <Link
                  to={href}
                  data-testid={`featured-work-${w.id}`}
                  data-orientation={vertical ? "vertical" : "horizontal"}
                  data-cursor="View"
                  className="group block w-full text-left"
                >
                  <div className={`relative overflow-hidden ${frame} bg-[#121214]`}>
                    <picture>
                      {w.cover_image_mobile && (
                        <source media="(max-width: 767px)" srcSet={w.cover_image_mobile} />
                      )}
                      <img
                        src={w.cover_image}
                        alt={w.title}
                        loading="lazy"
                        decoding="async"
                        width={vertical ? 1080 : 1600}
                        height={vertical ? 1920 : 900}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
                      />
                    </picture>
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/40" />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6 md:p-8">
                      <span className="overline text-[#C9A050]">View project →</span>
                    </div>
                  </div>
                  <div className="mt-6 flex items-end justify-between gap-6">
                    <div>
                      <p className="overline mb-3">
                        {w.category} <span className="mx-2 opacity-40">/</span> {w.year}
                      </p>
                      <h3 data-testid="featured-work-title" className="font-display text-2xl md:text-3xl tracking-tight">{w.title}</h3>
                      {w.client && <p className="text-[#8A8A93] text-sm mt-2">For {w.client}</p>}
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-32 md:mt-44 text-center">
          <Link
            to="/work"
            className="inline-flex items-center gap-5 group"
          >
            <span className="h-px w-16 bg-white/25 group-hover:bg-white/40 group-hover:w-24 transition-all duration-500" />
            <span className="overline text-white/80 group-hover:text-white transition-colors">View all projects</span>
            <span className="h-px w-16 bg-white/25 group-hover:bg-white/40 group-hover:w-24 transition-all duration-500" />
          </Link>
        </div>
      </section>

      {/* ---------- ABOUT STRIP ---------- */}
      <section data-testid="home-about" className="px-6 md:px-14 lg:px-24 py-36 md:py-56 border-t border-white/5">
        <div className="grid grid-cols-12 gap-6 md:gap-16">
          <div className="col-span-12 md:col-span-3">
            <Reveal>
              <p className="overline">The studio</p>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-9">
            <Reveal>
              <h3 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.035em]">
                {settings?.about_heading || "A studio,"}{" "}
                <span className="font-serif-accent text-[#C9A050]">{settings?.about_heading_italic || "not an agency."}</span>
              </h3>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-14 md:mt-20 text-[#C5C5CC] text-lg md:text-[22px] leading-[1.55] max-w-3xl font-light whitespace-pre-line">
                {settings?.about_body}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- MARKETING BAND — How we think (3-step) ---------- */}
      {settings?.home_marketing_visible !== false && <MarketingBand settings={settings} />}

      {/* ---------- NOTES BAND — latest blog posts ---------- */}
      {settings?.home_notes_visible !== false && posts.length > 0 && <NotesBand posts={posts} settings={settings} />}
    </main>
  );
}

/* ---------- Homepage marketing band: 3-step approach diagram ---------- */
function MarketingBand({ settings }) {
  const overline = settings?.home_marketing_overline || "Our approach";
  const heading = settings?.home_marketing_heading || "How we";
  const headingItalic = settings?.home_marketing_heading_italic || "think.";
  const paragraph = settings?.home_marketing_paragraph ||
    "Not one-off campaigns. A continuous content system — built around the audience, the channel, and the cadence that earns attention over time.";
  const steps = [
    {
      n: "01",
      title: settings?.home_marketing_step1_title || "Insight",
      body: settings?.home_marketing_step1_body || "Who is the audience and what truth pulls them in? We start with the question — never the asset.",
    },
    {
      n: "02",
      title: settings?.home_marketing_step2_title || "Distribution",
      body: settings?.home_marketing_step2_body || "Where they actually watch, scroll, listen. The story is shaped to the channel, not retro-fitted into it.",
    },
    {
      n: "03",
      title: settings?.home_marketing_step3_title || "System",
      body: settings?.home_marketing_step3_body || "A repeatable engine — content, voice, cadence — that compounds quietly across months, not weeks.",
    },
  ];
  return (
    <section data-testid="home-marketing-band" className="px-6 md:px-14 lg:px-24 py-32 md:py-52 border-t border-white/5 bg-[#0A0A0C]">
      <div className="grid grid-cols-12 gap-6 md:gap-16 mb-20 md:mb-28">
        <div className="col-span-12 md:col-span-3">
          <Reveal>
            <p className="overline">{overline}</p>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-9">
          <Reveal>
            <h3 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.035em]">
              {heading}{" "}
              <span className="font-serif-accent text-[#C9A050]">{headingItalic}</span>
            </h3>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 md:mt-14 text-[#A1A1AA] text-base md:text-lg leading-[1.7] max-w-2xl font-light whitespace-pre-line">
              {paragraph}
            </p>
          </Reveal>
        </div>
      </div>

      {/* 3-step diagram */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-y-16 md:gap-y-0 md:gap-x-6 items-stretch">
        {steps.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.1} className="col-span-12 md:col-span-4 relative">
            <div className="flex flex-col h-full">
              <div className="flex items-center gap-4 mb-7">
                <span className="font-serif-accent text-[#C9A050] text-3xl md:text-4xl leading-none">{s.n}</span>
                <span className="h-px flex-1 bg-gradient-to-r from-[#C9A050]/35 to-transparent" />
              </div>
              <h4 className="font-display text-3xl md:text-4xl tracking-[-0.02em] mb-4">{s.title}</h4>
              <p className="text-[#A1A1AA] text-base leading-[1.7] font-light max-w-md whitespace-pre-line">{s.body}</p>
            </div>
            {/* dotted gold connector → next step (desktop only) */}
            {i < steps.length - 1 && (
              <span
                aria-hidden="true"
                className="hidden md:block absolute top-[18px] -right-3 w-6 border-t border-dotted border-[#C9A050]/40"
              />
            )}
          </Reveal>
        ))}
      </div>

      <div className="mt-20 md:mt-32 text-center">
        <Link
          to="/marketing"
          data-testid="home-marketing-cta"
          data-cursor="Read"
          className="inline-flex items-center gap-5 group"
        >
          <span className="h-px w-16 bg-white/25 group-hover:bg-[#C9A050] group-hover:w-24 transition-all duration-500" />
          <span className="overline text-white/80 group-hover:text-[#C9A050] transition-colors">See our approach</span>
          <span className="h-px w-16 bg-white/25 group-hover:bg-[#C9A050] group-hover:w-24 transition-all duration-500" />
        </Link>
      </div>
    </section>
  );
}

/* ---------- Homepage notes band: swipeable carousel of published blogs ---------- */
function NotesBand({ posts, settings }) {
  const trackRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const scrollingRef = useRef(false);
  const overline = settings?.home_notes_overline || "From the studio";
  const heading = settings?.home_notes_heading || "Notes &";
  const headingItalic = settings?.home_notes_heading_italic || "long-form.";
  const paragraph = settings?.home_notes_paragraph ||
    "Long-form thinking on positioning, voice, distribution, and the small things that turn an audience into a brand.";

  const updateButtons = () => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth - 2;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < max);
  };

  useEffect(() => {
    updateButtons();
    const el = trackRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateButtons, { passive: true });
    window.addEventListener("resize", updateButtons);
    return () => {
      el.removeEventListener("scroll", updateButtons);
      window.removeEventListener("resize", updateButtons);
    };
  }, [posts.length]);

  // Throttle: native smooth-scroll cancels mid-flight if scrollBy() fires
  // again before the previous one finishes — felt "stuck" when reversing.
  const scrollByDir = (dir) => {
    if (scrollingRef.current) return;
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-blog-card]");
    const step = (card?.getBoundingClientRect().width || 360) + 24;
    scrollingRef.current = true;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
    setTimeout(() => {
      scrollingRef.current = false;
      updateButtons();
    }, 600);
  };

  return (
    <section data-testid="home-notes-band" className="px-6 md:px-14 lg:px-24 py-32 md:py-52 border-t border-white/5">
      <div className="grid grid-cols-12 gap-6 md:gap-16 mb-20 md:mb-28">
        <div className="col-span-12 md:col-span-3">
          <Reveal>
            <p className="overline">{overline}</p>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-9">
          <Reveal>
            <h3 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.035em]">
              {heading}{" "}
              <span className="font-serif-accent text-[#C9A050]">{headingItalic}</span>
            </h3>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 md:mt-14 text-[#A1A1AA] text-base md:text-lg leading-[1.7] max-w-2xl font-light whitespace-pre-line">
              {paragraph}
            </p>
          </Reveal>
        </div>
      </div>

      {/* Swipeable carousel — full bleed-ish on mobile, padded on desktop */}
      <div className="relative -mx-6 md:mx-0">
        {/* right-edge fade hint */}
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 bottom-0 w-12 md:w-20 pointer-events-none z-10"
          style={{ background: "linear-gradient(to left, #0A0A0C, transparent)" }}
        />
        {/* Floating arrow buttons — visible on all viewports, gold-bloom on hover */}
        <CarouselArrow
          dir="left"
          disabled={!canLeft}
          onClick={() => scrollByDir(-1)}
          testid="notes-arrow-left"
          floating
        />
        <CarouselArrow
          dir="right"
          disabled={!canRight}
          onClick={() => scrollByDir(1)}
          testid="notes-arrow-right"
          floating
        />
        <div
          ref={trackRef}
          data-testid="home-notes-track"
          className="flex gap-x-6 overflow-x-auto scrollbar-none snap-x snap-mandatory px-6 md:px-0 pb-2"
          style={{ WebkitOverflowScrolling: "touch", scrollPaddingLeft: "1.5rem" }}
        >
          {posts.map((p, i) => {
            const tag = (p.tags || [])[0];
            return (
              <Reveal key={p.slug} delay={(i % 3) * 0.08} className="snap-start shrink-0 w-[78vw] sm:w-[55vw] md:w-[calc((100%-3rem)/3)]">
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`home-note-${p.slug}`}
                  data-blog-card
                  data-cursor="Read"
                  className="group block"
                >
                  <div className="relative overflow-hidden aspect-[4/5] bg-[#121214]">
                    {p.cover_image ? (
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-[#1C1C1F] to-[#0A0A0C]" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/55" />
                  </div>
                  <div className="mt-6">
                    <p className="overline mb-3">
                      {tag || "Notes"}
                      <span className="mx-2 opacity-40">/</span>
                      {p.read_time_min ? `${p.read_time_min} min read` : "Read"}
                    </p>
                    <h4 className="font-display text-2xl md:text-3xl tracking-tight group-hover:text-[#C9A050] transition-colors">{p.title}</h4>
                    {p.excerpt && (
                      <p className="text-[#8A8A93] text-sm mt-3 leading-relaxed line-clamp-2">{p.excerpt}</p>
                    )}
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>

      <div className="mt-20 md:mt-32 text-center">
        <Link
          to="/blog"
          data-testid="home-notes-cta"
          data-cursor="Read"
          className="inline-flex items-center gap-5 group"
        >
          <span className="h-px w-16 bg-white/25 group-hover:bg-[#C9A050] group-hover:w-24 transition-all duration-500" />
          <span className="overline text-white/80 group-hover:text-[#C9A050] transition-colors">All notes</span>
          <span className="h-px w-16 bg-white/25 group-hover:bg-[#C9A050] group-hover:w-24 transition-all duration-500" />
        </Link>
      </div>
    </section>
  );
}

/* Carousel arrow — floats over carousel sides, gold bloom on hover.
   When `floating` is true: positioned absolute at left-3/right-3, vertically
   centered on the carousel track (≈40-45% from top of the section).
   When `floating` is false: legacy inline behaviour. */
function CarouselArrow({ dir, disabled, onClick, testid, floating = false }) {
  const positionCls = floating
    ? `absolute z-20 ${dir === "left" ? "left-3 md:left-5" : "right-3 md:right-5"} top-[35%] md:top-1/2 -translate-y-1/2`
    : "";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === "left" ? "Previous" : "Next"}
      data-testid={testid}
      className={`group/arrow ${positionCls} w-11 h-11 md:w-12 md:h-12 rounded-full border bg-black/55 backdrop-blur-sm transition-all duration-500 flex items-center justify-center ${
        disabled
          ? "opacity-25 cursor-not-allowed border-white/15"
          : "border-white/25 hover:border-[#C9A050] hover:bg-[#C9A050]/15 hover:scale-110 hover:shadow-[0_0_28px_rgba(201,160,80,0.55)]"
      }`}
    >
      <span
        className={`block w-3 h-3 border-t border-r border-white/85 transition-colors group-hover/arrow:border-[#C9A050] ${
          dir === "left" ? "-rotate-[135deg] translate-x-[1px]" : "rotate-45 -translate-x-[1px]"
        }`}
      />
    </button>
  );
}

/* ---------- Auto-cycling italic word — final clean version ----------
   Each word gets a permanent DOM element (no mount/unmount), opacity is
   toggled via pure CSS transition. NO Framer Motion → no GPU transform
   stacking context → italic 'd' swashes/ascenders never get subpixel
   clipped. SEQUENCED fade-out → swap → fade-in (not crossfade), so only
   ONE word is ever visible at a time → no ghost glyphs from longer words
   bleeding past the shorter word's right edge. */
function CyclingItalicWord({ words }) {
  const [i, setI] = useState(0);
  const [phase, setPhase] = useState("in"); // "in" = current visible | "out" = fading out
  const [fontReady, setFontReady] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts) {
      setFontReady(true);
      return undefined;
    }
    let cancelled = false;
    // Load BOTH 300 (used elsewhere) and 400 (cycling word weight) before showing
    Promise.all([
      document.fonts.load("italic 400 1em Fraunces"),
      document.fonts.load("italic 300 1em Fraunces"),
    ])
      .then(() => { if (!cancelled) setFontReady(true); })
      .catch(() => { if (!cancelled) setFontReady(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!fontReady || !words.length) return undefined;
    let swapTimer;
    const interval = setInterval(() => {
      setPhase("out");
      swapTimer = setTimeout(() => {
        setI((prev) => (prev + 1) % words.length);
        setPhase("in");
      }, 750);
    }, 2900);
    return () => {
      clearInterval(interval);
      clearTimeout(swapTimer);
    };
  }, [fontReady, words.length]);

  const widest = words.reduce((a, b) => (a.length > b.length ? a : b), "");
  const showCurrent = fontReady && phase === "in";

  return (
    <span
      className="relative inline-block align-baseline whitespace-nowrap font-serif-accent"
      style={{ overflow: "visible" }}
    >
      {/* Invisible ghost sizes the inline parent to the widest word */}
      <span aria-hidden className="invisible">{widest}</span>
      {/* All words rendered permanently. Only ONE has opacity 1 at any
         moment (the current, when phase==="in"). All others stay at 0.
         Font weight bumped to 400 + WONK disabled + geometricPrecision
         text-rendering → all glyph strokes (including the thin 'd' top
         hook) reach perceptible alpha at the same rate during fade-in. */}
      {words.map((word, idx) => (
        <span
          key={word}
          data-testid={idx === i ? `hero-cycle-word-${idx}` : undefined}
          aria-hidden={idx !== i}
          className="absolute left-0 top-0 text-[#C9A050] whitespace-nowrap"
          style={{
            overflow: "visible",
            opacity: showCurrent && idx === i ? 1 : 0,
            transition: "opacity 600ms cubic-bezier(0.4, 0, 0.2, 1)",
            fontWeight: 400,
            fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 0',
            textRendering: "geometricPrecision",
            // Critical: gives italic 'd' swash physical room within
            // the element's bounding box so GPU rasterization doesn't
            // clip it during opacity transitions.
            paddingRight: "0.5em",
            // Pre-allocate GPU layer with correct size from first paint
            // → eliminates first-load ghost from previous word's residual.
            willChange: "opacity",
          }}
        >
          {word}
        </span>
      ))}
    </span>
  );
}
